import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarDays, FileText, ShieldCheck } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { formatAed, formatDate, formatNumber, useLocale, useTranslation, type Locale } from "@ncp/i18n";
import { Badge, Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Field, SourceText, Textarea, Tooltip, useSave } from "@ncp/ui";
import type { TracedFigure } from "./figures";

/** The value as the figure's unit states it: dirhams in full, a share at its precision, an index as a whole number. */
export function figureValueText(row: Pick<TracedFigure, "value" | "unit">, locale: Locale): string {
  if (row.value == null) return "—";
  if (row.unit === "AED") return formatAed(row.value, locale);
  if (row.unit === "%") return `${formatNumber(row.value, locale, { maximumFractionDigits: 2 })}%`;
  return formatNumber(row.value, locale, { maximumFractionDigits: 0 });
}

/**
 * A figure as condition 2.3 asks: its value and status, the period it covers and the date its source was recorded, the document, page and
 * passage of a figure read from a document, and – for a calculated or extracted figure – whether an officer has checked it against its source.
 * An unchecked figure offers the check (a calculated one once the figures it rests on are checked); until it is recorded, no approved report,
 * decision or letter quotes the figure. `valueless` shows the status and source alone, where the value stands in another column. `declarant`
 * names who declared a declared figure on a file outside the concentrations – the applicant, the complainant or the parties – and says that
 * it was not checked against an independent source (never "verified").
 */
export function FigureValue({ caseId, row, display, valueless = false, declarant }: { caseId: string; row?: TracedFigure; display?: string; valueless?: boolean; declarant?: "applicant" | "complainant" | "parties" }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const ar = locale === "ar";
  const [open, setOpen] = useState(false);
  if (!row) return <span>{t("backoffice:r3Metrics.notRecorded")}</span>;
  const period = ar ? row.periodAr ?? row.period : row.period;
  const blockedBy = ar ? row.uncheckedInputsAr : row.uncheckedInputsEn;
  // A calculated figure whose inputs are unchecked waits for them: its own check would confirm the arithmetic, not the figures.
  const checkable = row.unchecked && row.status !== "NotRecorded" && blockedBy.length === 0 && (row.status === "Calculated" || row.status === "Extracted") && !row.checkedBy;
  return (
    <span className="inline-flex flex-col gap-1">
      <span className="inline-flex flex-wrap items-center gap-1.5">
        {valueless ? null : row.value != null ? (display ?? figureValueText(row, locale)) : t("backoffice:r3Metrics.notRecorded")}
        {row.value != null ? <Badge size="sm" variant="outline" title={ar ? row.sourceAr : row.sourceEn}>{declarant && row.status === "Declared" ? t(`backoffice:r3Metrics.figureDeclaredBy.${declarant}`) : t(`backoffice:r3Metrics.figureStatus.${row.status}`)}</Badge> : null}
        {row.unchecked ? <Badge size="sm" variant="warning">{t("backoffice:r3Metrics.figureCheck.unchecked")}</Badge> : null}
        {row.checkedBy ? <Badge size="sm" variant="success" title={row.checkNote ?? undefined}><ShieldCheck /> {t("backoffice:r3Metrics.figureCheck.checkedBy", { name: row.checkedBy, date: row.checkedAt ? formatDate(row.checkedAt, locale) : "—" })}</Badge> : null}
        {checkable ? <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => setOpen(true)}>{t("backoffice:r3Metrics.figureCheck.check")}</Button> : null}
      </span>
      {row.value != null && (period || row.sourceDate) ? (
        <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
          <CalendarDays className="size-3" />
          {[period, row.sourceDate ? t("backoffice:r3Metrics.figureCheck.recorded", { date: formatDate(row.sourceDate, locale) }) : null].filter(Boolean).join(" · ")}
        </span>
      ) : null}
      {row.value != null && row.documentName ? (
        <Tooltip content={row.excerpt ? <span className="block max-w-xs text-xs leading-relaxed"><SourceText text={`“${row.excerpt}”`} /></span> : (ar ? row.sourceAr : row.sourceEn)}>
          <span className="inline-flex cursor-help items-center gap-1 text-[11px] text-muted-foreground">
            <FileText className="size-3" />
            <bdi>{row.documentName}</bdi>{row.page != null ? ` · ${t("backoffice:r3Metrics.figureCheck.page", { page: row.page })}` : ""}
          </span>
        </Tooltip>
      ) : null}
      {declarant && row.value != null && row.status === "Declared" ? <span className="text-[11px] text-muted-foreground">{t("backoffice:r3Metrics.figureCheck.notIndependent")}</span> : null}
      {row.unchecked && blockedBy.length > 0 ? <span className="text-[11px] text-warning-800">{t("backoffice:r3Metrics.figureCheck.inputsUnchecked", { inputs: blockedBy.join(ar ? "، " : "; ") })}</span> : null}
      {open ? <CheckDialog caseId={caseId} row={row} onClose={() => setOpen(false)} /> : null}
    </span>
  );
}

function CheckDialog({ caseId, row, onClose }: { caseId: string; row: TracedFigure; onClose: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const qc = useQueryClient();
  const ar = locale === "ar";
  const [note, setNote] = useState("");
  const save = useSave({
    mutationFn: () => api.post<TracedFigure>(`/api/analysis/cases/${caseId}/figures/check`, { subjectKey: row.subjectKey, note }),
    saved: t("backoffice:r3Metrics.figureCheck.saved"),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["analysis", "figures", caseId] }); onClose(); },
  });
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{t("backoffice:r3Metrics.figureCheck.title")}</DialogTitle>
          <DialogDescription><SourceText text={ar ? row.nameAr : row.nameEn} /> · {figureValueText(row, locale)} · {ar ? row.sourceAr : row.sourceEn}</DialogDescription>
        </DialogHeader>
        {row.excerpt ? <blockquote className="border-s-2 border-sand-300 ps-3 text-xs leading-relaxed text-muted-foreground"><SourceText text={row.excerpt} /></blockquote> : null}
        <p className="text-xs text-muted-foreground">{t("backoffice:r3Metrics.figureCheck.hint")}</p>
        <Field label={t("backoffice:r3Metrics.figureCheck.note")} required>{(id) => <Textarea id={id} value={note} onChange={(e) => setNote(e.target.value)} rows={3} dir="auto" draft={`case:${caseId}:figure:${row.subjectKey}:check:note`} />}</Field>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("actions.cancel")}</Button>
          <Button variant="gold" loading={save.isPending} disabled={note.trim().length < 10} onClick={() => save.mutate()}><ShieldCheck /> {t("backoffice:r3Metrics.figureCheck.confirm")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
